import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, ExterneReferenz } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand,
  useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { monitorSprungUrl } from "../monitor.js";
import { KiKnopf, LoeschKnopf } from "../components/meHuellen.js";
import "./KundePortalMandanten.css";

/**
 * Der Abschnitt „Portal-Mandanten" in den Stammdaten eines Kunden (Spec „Mandantenabgleich
 * mit dem Monitor", 07.09.2026).
 *
 * Er beantwortet eine Frage, die sonst niemand beantworten kann: **welcher Kunde ist
 * welcher Mandant im Portal.** Der Monitor führt die fachlichen Bestände je Mandant und
 * kennt 381 davon; wie sie heissen und welche zählen, weiss allein dieses System. Was hier
 * eingetragen wird, holt der Monitor stündlich ab — und blendet aus, was hier fehlt.
 *
 * Deshalb steht an der Entfernen-Rückfrage genau das: Es ist keine Aufräumhandlung, sondern
 * eine, die drüben einen Mandanten aus der Beobachtung nimmt.
 *
 * **Ein eigener Block unter den Stammdaten, kein Teil von ihnen.** Die Referenzen sind
 * EIGENE Sätze — sie werden angelegt und entfernt, nicht mitgespeichert. Sie hängen deshalb
 * nicht am Speichern-Knopf der Stammdaten, brauchen keine Satzsperre (zwei Bearbeiter kommen
 * sich nicht in die Quere; meinen sie denselben Mandanten, entscheidet der eindeutige
 * Schlüssel in der Datenbank) und tragen kein `version`.
 *
 * **Eigener CSS-Block `kpm`** — kein geteilter Blockname über Ansichten hinweg (Lektion aus
 * `.ka__`). Er gehört allein dieser Datei.
 */

/** Nur die drei Klientenmethoden, die dieser Abschnitt braucht — so lässt er sich im Test
 *  mit drei vi.fn zeichnen (Muster KundeKonditionen/KundeStammdaten). */
export type PortalMandantenClient = Pick<ApiClient,
  "listExternalReferences" | "createExternalReference" | "deleteExternalReference">;

export type KundePortalMandantenProps = {
  client: PortalMandantenClient;
  /** Der Kunde, dessen Zuordnungen hier stehen. Ein Wechsel setzt ALLES zurück — siehe
   *  den Effekt unten. */
  customerId: string;
  /**
   * Über diesem Abschnitt steht ein FREMDES Panel — das Stammdaten-Formular. Dann ist
   * „Mandant zuordnen" nicht anklickbar, und ein Knopf, den niemand drücken kann, gehört
   * gesperrt (dieselbe Überlegung wie bei `verdeckt` in KundeKonditionen; in jsdom fängt
   * nichts den Klick ab, ein Test griffe sonst hinter das Panel).
   */
  verdeckt?: boolean;
};

/**
 * Der Wert der System-Auswahl, der „nicht aus der Liste" bedeutet.
 *
 * Freitext bleibt möglich (Entscheid Dirk 07.09.2026) — nur eben nicht als Vorgabefall:
 * Wer `portal-prod` meint, soll ihn anklicken und nicht abtippen (ein vertipptes
 * `portal-prd` ergäbe keinen Fehler, sondern eine Zuordnung, die niemand je abholt). Der
 * Wert kann mit KEINEM echten Systemschlüssel kollidieren, weil `^[A-Za-z0-9_-]{1,40}$`
 * keine Doppelpunkte zulässt.
 */
const ANDERES_SYSTEM = ":anderes:";

/**
 * Dieselbe Gestaltprüfung wie in `domain` (EXTERNAL_SCHLUESSEL_RE) — ABGESCHRIEBEN, weil
 * `web` `domain` nicht importieren darf (Grenzprüfer).
 *
 * Sie ist hier NICHT die Entscheidung, sondern nur die schnelle Rückmeldung: Der Server
 * prüft in jedem Fall noch einmal, und seine 422 gewinnt immer — sie landet an demselben
 * Feld und überschreibt, was hier steht. Der Sinn dieser Kopie ist der Weg dazwischen: Ein
 * aus dem Monitor kopierter Schlüssel bringt regelmässig ein Leerzeichen mit, und das soll
 * man sehen, bevor man speichert.
 *
 * Dass die beiden Fassungen gleich BLEIBEN, hält ein Test fest, der die Domänenquelle liest
 * (test/portalMandanten.test.tsx) — sonst wäre diese Kopie genau die Sorte stiller
 * Doppelung, gegen die die Hausregeln geschrieben sind.
 */
const SCHLUESSEL_RE = /^[A-Za-z0-9_-]{1,40}$/;

/** Der Hilfetext aus der Spec — er sagt, WO die Zahl steht, die hier hineingehört. */
const HILFETEXT =
  "Der Schlüssel steht im Monitor unter Verwaltung → Mandanten "
  + "(die Zahl aus dem Portal, z. B. 15554).";

/** Der Entwurf im Zuordnen-Panel. Alles Zeichenketten — der Schreibweg macht daraus null. */
type Entwurf = {
  /** Die Auswahl: ein Systemschlüssel oder ANDERES_SYSTEM. */
  wahl: string;
  /** Nur bei ANDERES_SYSTEM benutzt. */
  eigenesSystem: string;
  externalId: string;
  note: string;
};

type FeldName = "systemKey" | "externalId" | "note";

function leererEntwurf(vorgabe: string): Entwurf {
  return { wahl: vorgabe, eigenesSystem: "", externalId: "", note: "" };
}

/** Der tatsächlich gemeinte Systemschlüssel — aus der Auswahl oder aus dem Freitextfeld. */
function systemAus(e: Entwurf): string {
  return e.wahl === ANDERES_SYSTEM ? e.eigenesSystem.trim() : e.wahl;
}

/** Ob im Entwurf überhaupt etwas steht, das verloren gehen könnte. */
function etwasEingegeben(e: Entwurf, vorgabe: string): boolean {
  return e.externalId.trim() !== "" || e.note.trim() !== ""
    || e.eigenesSystem.trim() !== "" || e.wahl !== vorgabe;
}

export function KundePortalMandanten(p: KundePortalMandantenProps): ReactElement {
  /** `null` heisst „noch nicht geladen" — und ist von „geladen und leer" zu unterscheiden:
   *  „Noch keine Zuordnung" ist eine Aussage, die erst nach einer Antwort stimmt. */
  const [eintraege, setEintraege] = useState<ExterneReferenz[] | null>(null);
  const [vorschlaege, setVorschlaege] = useState<string[]>([]);
  /**
   * Die Basis-Adresse des Monitors für den Verweis „Im Monitor ansehen" (Spec
   * „Monitor-Mandantensicht", 08.09.2026, Stufe A) — `null` heisst: KEIN Verweis.
   *
   * Sie kommt vom SERVER und steht nirgends in dieser Oberfläche: Ein fest verdrahteter
   * Host wäre auf einer Testinstanz falsch und auf einem System ohne Monitor eine
   * Einladung ins Leere. Sie reist mit der Liste mit — aus demselben Grund wie die
   * Systemvorschläge daneben.
   */
  const [monitorBasisUrl, setMonitorBasisUrl] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  /** Meldung eines gescheiterten Entfernens (ohne Panel, im Abschnitt). */
  const [meldung, setMeldung] = useState<string | null>(null);

  /** Der Vorgabewert der Auswahl: der erste Vorschlag (portal-prod), solange es einen gibt. */
  const vorgabe = vorschlaege[0] ?? ANDERES_SYSTEM;

  /**
   * Die laufende NUMMER dieser Akte — der Riegel gegen Antworten, die zu spät kommen.
   *
   * Jeder Abruf merkt sich beim Start die Nummer und schreibt sein Ergebnis nur, wenn sie
   * beim Eintreffen noch dieselbe ist. Der Kundenwechsel (und das Abräumen beim Ausbauen)
   * zählt sie hoch und macht damit ALLE noch fliegenden Antworten ungültig — nicht nur die
   * des Lade-Effekts, sondern auch die Nachlade-Abrufe nach Zuordnen und Entfernen. Genau
   * die waren die Lücke: Sie hingen am `customerId` ihrer Closure und schrieben ihre Liste
   * ungefragt in den Zustand, auch wenn inzwischen ein anderer Kunde auf dem Bildschirm
   * stand — der nächste Blick zeigte dann die Mandanten des vorigen Kunden (Fund der
   * Prüfung vom 07.09.2026).
   *
   * Eine Ref und kein Zustand: Sie darf kein Neuzeichnen auslösen, und sie muss beim
   * Auswerten den AKTUELLEN Wert haben, nicht den der Render-Closure.
   */
  const lauf = useRef(0);

  const { client, customerId } = p;
  const listExternalReferences = client.listExternalReferences;

  /*
   * Laden — und zugleich der VOLLSTÄNDIGE Rückbau beim Kundenwechsel.
   *
   * KEIN ZUSTAND ÜBERLEBT DEN KONTEXTWECHSEL (Hausregel): Wechselt die Akte, fallen die
   * Liste, ein offenes Panel, jede Meldung und jeder Feldfehler weg. Ohne diesen Rückbau
   * stünde ein halb ausgefülltes „Mandant zuordnen" nach dem Wechsel über einem FREMDEN
   * Kunden — und ein Klick auf Speichern hängte den Mandanten an den falschen. Der
   * `abgebrochen`-Riegel deckt den Fall, dass die Antwort des vorigen Kunden erst nach dem
   * Wechsel eintrifft.
   */
  useEffect(() => {
    const meiner = ++lauf.current;
    const nochAktuell = (): boolean => meiner === lauf.current;
    setEintraege(null);
    setLadeFehler(null);
    setDialog(null);
    setMeldung(null);
    setMonitorBasisUrl(null);
    listExternalReferences(customerId)
      .then((antwort) => {
        if (!nochAktuell()) return;
        setEintraege(antwort.items);
        setMonitorBasisUrl(antwort.monitorBasisUrl ?? null);
        // `?? []` gegen eine Antwort, die den Vertrag nicht hält: Der echte Server liefert
        // die Liste immer (Pflichtfeld, vom Antwortschema erzwungen), aber ein Ausfall
        // dieses einen Feldes darf nicht die GANZE Kundenakte weissen Bildschirm zeigen —
        // ohne Vorschläge bleibt „Anderes System …" übrig, und damit lässt sich arbeiten.
        setVorschlaege(antwort.systemVorschlaege ?? []);
      })
      .catch((fehler: unknown) => {
        if (!nochAktuell()) return;
        setEintraege([]);
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Portal-Mandanten konnten nicht geladen werden.");
      });
    // Auch das ABRÄUMEN zählt hoch: Beim Ausbauen der Ansicht sind alle fliegenden
    // Antworten ungültig, nicht erst beim nächsten Kunden.
    return () => { lauf.current += 1; };
  }, [listExternalReferences, customerId]);

  const geaendert = dialog !== null && etwasEingegeben(dialog, vorgabe);
  // Auch das ECHTE Verlassen (Tab zu, neu laden) fragt nach — wie an jeder anderen Maske.
  useBeforeUnload(geaendert);

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 3). Anlegen ohne Versionsvergleich — keine 409.
   * Die eigene Prüfung VOR dem Absenden (schnelle Rückmeldung, nicht die Entscheidung)
   * geht als 422-Gestalt in denselben Weg wie die des Servers: Fehler am Feld, Sprung zum
   * ersten, kein Aufruf. DIE DOPPELZUORDNUNG gehört an die Kennung: Sie kommt als 422 mit
   * dem NAMEN des anderen Kunden und dem Feldbezug `externalId`.
   */
  const formular = useFormularZustand<Awaited<ReturnType<typeof listExternalReferences>> | null>({
    speichern: async () => {
      const e = dialog;
      if (e === null) return null;
      const eigene = eigenePruefung(e);
      if (Object.keys(eigene).length > 0) {
        throw new ApiError(422, "EINGABE_UNGUELTIG", "Bitte die Eingaben prüfen.",
          Object.entries(eigene).map(([field, message]) => ({ field, message })));
      }
      const notiz = e.note.trim();
      await client.createExternalReference(customerId, {
        systemKey: systemAus(e),
        externalId: e.externalId.trim(),
        note: notiz === "" ? null : notiz,
      });
      // Nach dem Erfolg den SERVERSTAND holen statt die Antwort anzuhängen: Die Liste ist
      // sortiert (System, dann Schlüssel), und ein angehängter Satz stünde am falschen
      // Platz — bis zum nächsten Laden, das ihn wortlos verschöbe.
      return listExternalReferences(customerId);
    },
    neuLaden: async () => { /* keine 409 auf diesem Weg */ },
    bekannteFelder: ["systemKey", "externalId", "note"] satisfies FeldName[],
  });
  const speichert = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<FeldName, string>>;

  function oeffnen(): void {
    setDialog(leererEntwurf(vorgabe));
    formular.zuruecksetzen();
  }

  /**
   * Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"): kein Ausgang
   * unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. Knopf und Escape
   * gehen durch diese Funktion.
   */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }

  /** Die Prüfung vor dem Absenden — schnelle Rückmeldung, nicht die Entscheidung. */
  function eigenePruefung(e: Entwurf): Partial<Record<FeldName, string>> {
    const fehler: Partial<Record<FeldName, string>> = {};
    const systemKey = systemAus(e);
    if (systemKey === "") {
      fehler.systemKey = "Bitte ein System wählen oder eintragen.";
    } else if (!SCHLUESSEL_RE.test(systemKey)) {
      fehler.systemKey = "Nur Buchstaben, Ziffern, Bindestrich und Unterstrich "
        + "(höchstens 40 Zeichen).";
    }
    const schluessel = e.externalId.trim();
    if (schluessel === "") {
      fehler.externalId = "Der Mandantenschlüssel ist erforderlich.";
    } else if (!SCHLUESSEL_RE.test(schluessel)) {
      fehler.externalId = "Nur Buchstaben, Ziffern, Bindestrich und Unterstrich "
        + "(höchstens 40 Zeichen) — Leerzeichen kommen oft aus der Zwischenablage mit.";
    }
    return fehler;
  }

  async function beiSpeichern(): Promise<void> {
    const meiner = lauf.current;
    const frisch = await formular.aktion();
    // Wechselte die Akte, während geschrieben und nachgeladen wurde, gehört diese Liste
    // einem anderen Kunden — sie darf den Zustand nicht mehr anfassen.
    if (frisch === undefined || frisch === null || meiner !== lauf.current) return;
    setEintraege(frisch.items);
    setVorschlaege(frisch.systemVorschlaege ?? []);
    setMonitorBasisUrl(frisch.monitorBasisUrl ?? null);
    setDialog(null);
  }

  const { bestaetigen } = useBestaetigung();
  async function beiEntfernen(eintrag: ExterneReferenz): Promise<void> {
    /*
     * Die RÜCKFRAGE sagt, was drüben geschieht — nicht bloss „wirklich löschen?". Das
     * Entfernen ist keine Aufräumhandlung: Der Monitor nimmt den Mandanten danach aus der
     * Beobachtung, und wer das nicht weiss, klickt es weg.
     */
    const sicher = await bestaetigen({
      titel: "Zuordnung entfernen?",
      text: `Zuordnung entfernen: Mandant ${eintrag.externalId} (${eintrag.systemKey}). `
        + "Der Monitor blendet diesen Mandanten danach aus. Wirklich entfernen?",
      bestaetigenText: "Entfernen",
      gefaehrlich: true,
    });
    if (!sicher) return;
    const meiner = lauf.current;
    setMeldung(null);
    try {
      await client.deleteExternalReference(customerId, eintrag.id);
      const frisch = await listExternalReferences(customerId);
      if (meiner !== lauf.current) return; // dieselbe Lage wie beim Zuordnen
      setEintraege(frisch.items);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      // Auch hier an der KENNUNG: Die Meldung nennt den Mandanten, um den es ging.
      const grund = fehler instanceof ApiError ? fehler.message : "Unerwarteter Fehler.";
      setMeldung(`Mandant ${eintrag.externalId} konnte nicht entfernt werden. ${grund}`);
    }
  }

  return (
    <section className="kpm" aria-label="Portal-Mandanten">
      <h3 className="kpm__titel">Portal-Mandanten</h3>
      <p className="kpm__hinweis">
        Der Monitor beobachtet die Bestände dieses Kunden im Portal — aber nur die
        Mandanten, die hier zugeordnet sind.
      </p>

      {meldung !== null && dialog === null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}

      {eintraege === null && <p className="kpm__hinweis" role="status">Lädt …</p>}

      {eintraege !== null && eintraege.length === 0 && ladeFehler === null && (
        <p className="kpm__hinweis">
          Diesem Kunden ist noch kein Portal-Mandant zugeordnet. Er ist für den Monitor
          damit unsichtbar.
        </p>
      )}

      {eintraege !== null && eintraege.length > 0 && (
        <ul className="kpm__liste">
          {/* `key` am OBJEKT (seiner Kennung), nicht am Listenplatz: Die Liste ist
              sortiert und rückt beim Entfernen zusammen — ein Index-Schlüssel hängte den
              Zustand der Zeile an die falsche. */}
          {eintraege.map((r) => {
            // EINMAL gerechnet und nicht zweimal im JSX — die Adresse ist dieselbe, ob
            // gefragt wird, OB es sie gibt, oder wohin sie zeigt.
            const monitorZiel = monitorSprungUrl(monitorBasisUrl, r.externalId);
            return (
            <li key={r.id} className="kpm__zeile">
              <Chip className="kpm__system">{r.systemKey}</Chip>
              <span className="kpm__schluessel">{r.externalId}</span>
              <span className="kpm__notiz">{r.note ?? ""}</span>
              {/* Der Sprung in den Monitor (Stufe A). Neues Fenster mit
                  rel="noopener noreferrer": `noopener` nimmt der Zielseite das
                  `window.opener`, mit dem sie diese Anwendung umnavigieren könnte;
                  `noreferrer` hält den Referrer zurück — die Adresse dieser Seite trägt die
                  Kundenkennung. Die ANMELDUNG macht der Monitor selbst (Keycloak, SSO); von
                  hier geht nichts mit. Ohne eingerichtete Basis-Adresse steht hier NICHTS,
                  und nicht etwa ein toter Knopf. */}
              {monitorZiel !== null && (
                <a
                  className="kpm__verweis"
                  href={monitorZiel}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Mandant ${r.externalId} im Monitor ansehen`}
                >
                  Im Monitor ansehen
                </a>
              )}
              <LoeschKnopf
                className="kpm__knopf"
                name={`Zuordnung von Mandant ${r.externalId} in ${r.systemKey} entfernen`}
                deaktiviert={dialog !== null || p.verdeckt === true}
                onClick={() => { void beiEntfernen(r); }}
              />
            </li>
            );
          })}
        </ul>
      )}

      <div className="kpm__aktionen">
        <KiKnopf
          className="kpm__knopf"
          deaktiviert={eintraege === null || dialog !== null || p.verdeckt === true}
          onClick={oeffnen}
        >
          Mandant zuordnen
        </KiKnopf>
      </div>

      {dialog !== null && (
        <Modal titel="Mandant zuordnen" onAbbrechen={abbrechen}
          abbrechenGesperrt={speichert}>
          {/* Der Inhalt trägt seinen eigenen senkrechten Abstand: Der gemeinsame Rahmen
              staffelt seine Kinder nicht (siehe Modal.css). */}
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps}>
            <Auswahl id="kpm-system" name="kpm-system" beschriftung="System"
              wert={dialog.wahl}
              {...(feldFehler.systemKey !== undefined ? { fehler: feldFehler.systemKey } : {})}
              onGeaendert={(d) => setDialog((alt) => (alt === null ? alt
                : { ...alt, wahl: d.wert }))}>
              {vorschlaege.map((s) => <option key={s} value={s}>{s}</option>)}
              {/* Freitext bleibt möglich — als eigener Eintrag und nicht als Vorgabe. */}
              <option value={ANDERES_SYSTEM}>Anderes System …</option>
            </Auswahl>

            {dialog.wahl === ANDERES_SYSTEM && (
              <Feld
                id="kpm-eigenesSystem"
                label="Systemschlüssel"
                wert={dialog.eigenesSystem}
                bearbeitbar
                onChange={(wert) =>
                  setDialog((d) => (d === null ? d : { ...d, eigenesSystem: wert }))}
              />
            )}

            <Feld
              id="kpm-externalId"
              label="Mandantenschlüssel"
              wert={dialog.externalId}
              bearbeitbar
              onChange={(wert) =>
                setDialog((d) => (d === null ? d : { ...d, externalId: wert }))}
              {...(feldFehler.externalId !== undefined
                ? { fehler: feldFehler.externalId } : {})}
              hilfe={HILFETEXT}
            />

            <Feld
              id="kpm-note"
              label="Notiz"
              wert={dialog.note}
              bearbeitbar
              onChange={(wert) => setDialog((d) => (d === null ? d : { ...d, note: wert }))}
              {...(feldFehler.note !== undefined ? { fehler: feldFehler.note } : {})}
            />
          </Formular>

          {/* Die zwei einzigen Ausgänge. `abbrechenGesperrt` steht an BEIDEN Stellen —
              am Rahmen für die Escape-Taste, hier für den Knopf (Fixrunde 1, 05.09.2026:
              ein Riegel nur am Knopf liesse die Tastatur daran vorbei). */}
          <ModalFuss speichernText="Zuordnen"
            speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiSpeichern(); }}
            onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}
